import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  PlusIcon, 
  MinusIcon, 
  ChevronUpIcon, 
  ChevronDownIcon, 
  CalendarIcon, 
  Download, 
  AlertCircle, 
  Settings, 
  Trash2 as TrashIcon,
  Mail,
  Save,
  FileDown,
  Loader2
} from "lucide-react";
import { format } from "date-fns";
import { LoadingSpinner } from "./ui/loading-spinner";
import PracticeSettings from './PracticeSettings'; // Ensure correct import path
import { Alert, AlertTitle, AlertDescription } from "./ui/alert";
import { useToast } from "../hooks/use-toast";
import { SimpleCalendar } from "./ui/simple-calendar";
import { generateInvoicePDF } from '../services/InvoicePDFGenerator';  // Fix import path
import { getAuth } from 'firebase/auth';
import { invoiceNumberService } from '../services/invoiceNumberService'; // Add this import to fix the errors

// Create a version outside the component to prevent recreation
const EMPTY_ARRAY = [];

// Create a pure component for the MonthlyInvoice to prevent unnecessary re-renders
const MonthlyInvoice = (props) => {
  // ANTI-LOOP: Use a ref for render counting without causing re-renders
  const renderCount = useRef(0);
  renderCount.current += 1;
  
  // ANTI-LOOP: Log only occasionally to reduce side effects
  if (renderCount.current === 1 || renderCount.current % 10 === 0) {
    console.log(`MonthlyInvoice render #${renderCount.current}`);
  }

  // ANTI-LOOP: Destructure props outside of render to reduce lookups and make dependencies clearer
  const { 
    selectedPractices = EMPTY_ARRAY, 
    selectedPractice, 
    getPractice,
    user, 
    profileData,
    entries,
    setEntries,
    rateType,
    setRateType,
    rateValue,
    setRateValue,
    total,
    setTotal,
    summary,
    setSummary,
    handleAddEntry,
    handleRemoveEntry,
    handleEntryChange,
    handlePatientChange,
    handleAddPatient,
    handleRemovePatient,
    handleToggleEntry,
    handleDateChange,
    handleCalculateTotal,
    handleSaveInvoice,
    handleSendInvoice,
    handlePracticeChange,
    handleShowSettings,
    handlePracticeSelection,
    formatCurrency,
    showSettings,
    setShowSettings,
    previewHtml,
    setPreviewHtml,
    invoiceLoading,
    emailLoading,
    setEmailLoading,  // Add this to props
    previewLoading: externalPreviewLoading,
    setPreviewLoading: setExternalPreviewLoading,
    showEmailModal,
    setShowEmailModal,
    cn,
    pdfLoading: externalPdfLoading,
    setPdfLoading: setExternalPdfLoading,
  } = props;

  const auth = getAuth(); // Add this line
  // Add state for validation error
  const [validationError, setValidationError] = useState('');
  const [practiceDetails, setPracticeDetails] = useState(null);
  const [showPracticeSettings, setShowPracticeSettings] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(null);
  const { toast } = useToast();
  const [localPreviewLoading, setLocalPreviewLoading] = useState(false);
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState(null); // Add this line
  const [localPdfLoading, setLocalPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoiceSaved, setInvoiceSaved] = useState(false);
  const [lastSavedData, setLastSavedData] = useState(null);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState(null); // Add this line
  const [previewGenerated, setPreviewGenerated] = useState(false); // Add this line
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [emailPdfFilename, setEmailPdfFilename] = useState('');
  const [showPdfRequiredDialog, setShowPdfRequiredDialog] = useState(false);
  const [pdfGenerationStatus, setPdfGenerationStatus] = useState('not_started'); // Add this line

  // Add states for the progress bar UI and tracking
  const [pdfGenerationStage, setPdfGenerationStage] = useState('not_started'); // 'not_started', 'preparing', 'generating', 'downloading', 'completed', 'failed'
  const [pdfGenerationProgress, setPdfGenerationProgress] = useState(0); // 0 to 100
  const [pdfProgressTimer, setPdfProgressTimer] = useState(null);

  // Use either external or local loading state
  const previewLoading = externalPreviewLoading !== undefined ? externalPreviewLoading : localPreviewLoading;
  const setPreviewLoading = setExternalPreviewLoading || setLocalPreviewLoading;
  const pdfLoading = externalPdfLoading !== undefined ? externalPdfLoading : localPdfLoading;
  const setPdfLoading = setExternalPdfLoading || setLocalPdfLoading;
  
  // Clear validation error when rate value or type changes
  useEffect(() => {
    if (validationError) {
      setValidationError('');
    }
  }, [rateType, rateValue]);
  
  // Wrap calculate total with validation
  const validateAndCalculateTotal = useCallback(() => {
    // Check if rate value is provided
    if (!rateValue || rateValue.trim() === '') {
      setValidationError(`Please enter a ${rateType === 'percentage' ? 'percentage' : 'rate'} value`);
      return;
    }
    
    // Validate entries have dates
    const entriesWithoutDates = entries.filter(entry => !entry.date);
    if (entriesWithoutDates.length > 0) {
      setValidationError('Please add dates to all entries');
      return;
    }
    
    // Clear validation error and call the original handler
    setValidationError('');
    handleCalculateTotal();
  }, [rateValue, entries, handleCalculateTotal]);

  // Fix for uncontrolled to controlled inputs
  // Initialize description in entry
  useEffect(() => {
    const updatedEntries = entries.map(entry => {
      // Make sure all entries have description initialized
      if ((rateType === 'hourly' || rateType === 'fixed') && entry.description === undefined) {
        return { ...entry, description: '' };
      }
      
      // Make sure all patients have required fields initialized
      if (entry.patients) {
        const updatedPatients = entry.patients.map(patient => ({
          count: patient.count || '',
          rate: patient.rate || '',
          description: patient.description || '',
        }));
        return { ...entry, patients: updatedPatients };
      }
      return entry;
    });
    
    // Only update if there were changes
    if (JSON.stringify(updatedEntries) !== JSON.stringify(entries)) {
      setEntries(updatedEntries);
    }
  }, [entries, rateType]);

  // Load practice details when selectedPractice changes
  useEffect(() => {
    if (!selectedPractice) {
      setPracticeDetails(null);
      return;
    }

    // Find practice in props
    const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
    if (practice) {
      setPracticeDetails(practice);
    } else {
      setPracticeDetails(null);
      toast({
        title: "Practice Not Found",
        description: "Selected practice not found in available practices",
        variant: "destructive"
      });
    }
  }, [selectedPractice, selectedPractices, toast]);
  
  // Simplified date picker component
  const EnhancedDatePicker = ({ selected, onSelect, entryIndex }) => {
    return (
      <div className="p-3 bg-[#2A2A3E] text-white rounded-lg">
        <div className="calendar-wrapper">
          <SimpleCalendar
            value={selected}
            onChange={onSelect}
            className="bg-[#2A2A3E] text-white border-none"
            onClose={() => setCalendarOpen(null)}
          />
        </div>
      </div>
    );
  };

  // Handle saving practices from practice settings popup
  const handleSavePractices = (practices) => {
    // Pass to the parent component for handling
    handlePracticeSelection(practices);
    setShowPracticeSettings(false);
  };

  // Modified email invoice function to check PDF existence first
  const handleEmailInvoice = async () => {
    try {
      // Check if PDF has been generated already
      const hasPdfReady = previewGenerated && invoiceSaved && currentInvoiceNumber;
      const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
      
      if (!practice) {
        throw new Error('Practice not found');
      }
      
      // Calculate the PDF filename in advance
      const practiceName = practice.prac_name.replace(/\s+/g, '_');
      const pdfFilename = currentInvoiceNumber ? 
        `${practiceName}_invoice_${currentInvoiceNumber}.pdf` : '';
      
      // Set the filename and show dialog
      setEmailPdfFilename(pdfFilename);
      setShowEmailPrompt(true);
    } catch (error) {
      console.error('Error preparing email workflow:', error);
      toast({
        title: "Error",
        description: "Failed to start email workflow: " + error.message,
        variant: "destructive",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // New function to handle PDF generation when requested from dialog
  const handleGeneratePDFFromDialog = async () => {
    setShowPdfRequiredDialog(false);
    setPdfLoading(true);
    
    try {
      // Generate preview first if needed
      if (!previewGenerated) {
        await handleGeneratePreview();
      }
      
      // Then generate PDF
      await handleGeneratePDF();
      
      // Now show the email prompt
      await handleEmailInvoice();
    } catch (error) {
      console.error('Error generating PDF from dialog:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF: " + error.message,
        variant: "destructive",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Function to generate PDF from email dialog with improved progress tracking
  const handleGeneratePDFFromEmailDialog = async () => {
    // First, completely close the dialog to reset its state
    setShowEmailPrompt(false);
    
    // Give React time to process the state change and unmount the dialog
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Now start the PDF generation process
    setPdfLoading(true);
    startProgressTracking('preparing');
    
    try {
      // Generate preview first if needed
      if (!previewGenerated) {
        await new Promise(resolve => setTimeout(resolve, 800));
        await handleGeneratePreview();
        setPdfGenerationProgress(20);
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Update to generating stage with deliberate pause
      setPdfGenerationStage('generating');
      setPdfGenerationProgress(30);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Then generate PDF - this also saves the invoice and sets currentInvoiceNumber
      await handleGeneratePDF();
      
      // Update to the downloading stage with deliberate pause
      setPdfGenerationStage('downloading');
      setPdfGenerationProgress(75);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // After PDF generation completes, get the practice again
      const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
      if (!practice) {
        throw new Error('Practice not found');
      }
      
      // Now that we have the invoice number from saving, update filename
      if (currentInvoiceNumber) {
        const practiceName = practice.prac_name.replace(/\s+/g, '_');
        const newPdfFilename = `${practiceName}_invoice_${currentInvoiceNumber}.pdf`;
        
        // Update the filename state variable
        setEmailPdfFilename(newPdfFilename);
        
        // Complete the progress tracking
        setPdfGenerationStage('completed');
        setPdfGenerationProgress(100);
        
        // Let it settle before showing the dialog again
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Now reopen the dialog with everything fully ready
        setShowEmailPrompt(true);
        
        toast({
          title: "PDF Generated",
          description: "PDF has been downloaded. You can now open the email template.",
          variant: "success",
        });
      }
    } catch (error) {
      console.error('Error generating PDF from email dialog:', error);
      setPdfGenerationStage('failed');
      setPdfGenerationProgress(100);
      
      toast({
        title: "Error",
        description: "Failed to generate PDF: " + error.message,
        variant: "destructive",
      });
    } finally {
      // Don't immediately clear the loading state
      setTimeout(() => {
        setPdfLoading(false);
      }, 1000);
    }
  };

  // Function to open email client from dialog
  const openEmailTemplate = () => {
    try {
      // Get practice details
      const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
      if (!practice) {
        throw new Error('Practice not found');
      }

      // Create email subject with invoice number
      const subject = `Invoice ${currentInvoiceNumber} from ${profileData?.name || 'Dental Hygienist'}`;

      // Create detailed email body with invoice summary and attachment instructions
      const totalAmount = total?.amount ? formatCurrency(total.amount) : "£0.00";
      const emailBody = 
`⚠️ IMPORTANT: Please attach the PDF invoice named "${emailPdfFilename}" to this email before sending. ⚠️

Dear ${practice.prac_name},

Please find details regarding Invoice #${currentInvoiceNumber} attached to this email.

Invoice Summary:
- Invoice Number: ${currentInvoiceNumber}
- Date: ${format(new Date(), 'dd MMM yyyy')}
- Total Amount: ${totalAmount}
${summary && summary.length > 0 ? '- Treatment Dates: ' + summary.map(s => s.label).join(', ') : ''}

Payment Information:
Bank: ${profileData?.bankName || ''}
Sort Code: ${profileData?.sortCode || ''}
Account Number: ${profileData?.accountNumber || ''}

Payment is due within 30 days from date of invoice.

Kind regards,
${profileData?.name || ''}`;

      // Create mailto URL
      const mailtoUrl = `mailto:${encodeURIComponent(practice.email || '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

      // Open email client
      window.location.href = mailtoUrl;
      setShowEmailPrompt(false);
    } catch (error) {
      console.error('Error opening email template:', error);
      toast({
        title: "Error",
        description: "Failed to open email template: " + error.message,
        variant: "destructive",
      });
    }
  };

  // For preview invoice number functionality, modify handleGeneratePreview:
  const handleGeneratePreview = async () => {
    if (!profileData) {
      toast({
        title: "Error",
        description: "Please complete your profile before generating an invoice",
        variant: "destructive",
      });
      return;
    }
  
    if (!total) {
      toast({
        title: "Error",
        description: "Please calculate the total first",
        variant: "destructive",
      });
      return;
    }
  
    setPreviewLoading(true);
    setPreviewGenerated(false); // Reset when generating new preview
  
    try {
      // Get practice details
      const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
      if (!practice) {
        throw new Error('Practice not found');
      }
  
      // Get next invoice number WITHOUT incrementing counter
      let invoiceNum;
      try {
        invoiceNum = await invoiceNumberService.getPreviewNumber(user.uid, profileData);
        setPreviewInvoiceNumber(invoiceNum); // Cache the preview number
        setCurrentInvoiceNumber(null); // Don't store as current yet
      } catch (error) {
        console.error("Error getting preview number:", error);
        invoiceNum = 'PREVIEW';
      }
  
      // Format the invoice date
      const invoiceDate = format(new Date(), "dd MMMM yyyy");
  
      // Format practice address - filter out NA values
      const addressParts = [
        practice.prac_name && `<strong>${practice.prac_name}</strong>`,
        practice.address1 !== 'NA' && practice.address1,
        practice.address2 !== 'NA' && practice.address2,
        practice.address3 !== 'NA' && practice.address3,
        practice.address4 !== 'NA' && practice.address4,
        practice.address5 !== 'NA' && practice.address5,
        practice.prac_city !== 'NA' && practice.prac_city,
        (practice.prac_postcode || practice.postcode) !== 'NA' && (practice.prac_postcode || practice.postcode)
      ].filter(Boolean);
  
      // Generate the HTML content with proper performance
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
            <div>
              <h2 style="color: #4b0082; margin: 0;">${profileData.name || 'Your Name'}</h2>
              <p style="margin: 5px 0; color: #333;">
                ${profileData.address1 || ''}<br>
                ${profileData.city || ''}, ${profileData.postcode || ''}
              </p>
              ${profileData.email ? `<p style="margin: 5px 0; color: #333;">Email: ${profileData.email}</p>` : ''}
              ${profileData.phone ? `<p style="margin: 5px 0; color: #333;">Phone: ${profileData.phone}</p>` : ''}
            </div>
            <div style="text-align: right;">
              <h1 style="color: #4b0082; margin: 0;">INVOICE</h1>
              <p style="margin: 5px 0; color: #333;">Date: ${invoiceDate}</p>
              <p style="margin: 5px 0; color: #333;">Invoice #: ${invoiceNum}</p>
            </div>
          </div>
  
          <div style="background-color: #f9f9f9; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
            <h3 style="margin: 0 0 10px; color: #4b0082;">Bill To:</h3>
            <p style="margin: 0; color: #333;">
              ${addressParts.join('<br>')}
            </p>
          </div>
  
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
            <thead>
              <tr style="background-color: #4b0082; color: white;">
                <th style="padding: 10px; text-align: left;">Date</th>
                <th style="padding: 10px; text-align: left;">Description</th>
                <th style="padding: 10px; text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${summary.map(item => {
                const isSpecialRow = item.isSubtotal || item.isPercentage || item.isTotal;
                const rowStyle = isSpecialRow 
                  ? 'border-top: 2px solid #4b0082; background-color: #f9f9f9; font-weight: bold;'
                  : 'border-bottom: 1px solid #ddd;';
                
                return `
                  <tr style="${rowStyle}">
                    <td style="padding: 10px; ${isSpecialRow ? 'font-weight: bold;' : ''}">${item.label}</td>
                    <td style="padding: 10px; ${isSpecialRow ? 'font-weight: bold;' : ''}">${isSpecialRow ? '' : item.value.split('=')[0]}</td>
                    <td style="padding: 10px; text-align: right; ${isSpecialRow ? 'font-weight: bold;' : ''}">£${item.value.split('=')[1]?.trim()?.replace('£', '') || '0.00'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
  
          <div style="text-align: right; margin-bottom: 40px;">
            <h3 style="color: #4b0082; margin: 0;">Total: ${formatCurrency(total.amount)}</h3>
          </div>
        </div>
      `;
  
      setPreviewHtml(htmlContent);
      setPreviewGenerated(true); // Set to true on successful preview
      
      toast({
        title: "Success",
        description: "Invoice preview has been generated",
        variant: "success",
      });
    } catch (error) {
      console.error('Error generating preview:', error);
      setPreviewGenerated(false);
      setPreviewInvoiceNumber(null); // Clear cached number on error
      toast({
        title: "Error",
        description: "Failed to generate invoice preview",
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Format entries for PDF generation
  const formatEntriesForPDF = (entries) => {
    // Group entries by date first
    const entriesByDate = entries.reduce((acc, entry) => {
      if (!entry.date) return acc;
      
      const dateKey = format(entry.date, "yyyy-MM-dd");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(entry);
      return acc;
    }, {});

    // Convert grouped entries to formatted entries
    return Object.entries(entriesByDate).map(([dateKey, dateEntries]) => {
      const date = dateEntries[0].date;
      const formattedDate = format(date, "dd MMM yyyy");

      if (rateType === 'hourly' || rateType === 'fixed') {
        // Sum up hours for all entries on this date
        const hours = dateEntries.reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0);
        const amount = rateType === 'hourly' 
          ? hours * parseFloat(rateValue || 0)
          : parseFloat(rateValue || 0);

        return {
          date: formattedDate,
          formattedDate,
          description: dateEntries[0].description || 'Hours worked',
          amount,
          hours,
          patients: [{
            count: hours,
            rate: parseFloat(rateValue || 0),
            description: dateEntries[0].description || 'Hours worked'
          }],
          rateType,
          rateValue: parseFloat(rateValue || 0)
        };
      } else {
        // For percentage rates, collect all patients
        let totalAmount = 0;
        const allPatients = [];

        dateEntries.forEach(entry => {
          if (entry.patients && entry.patients.length > 0) {
            entry.patients.forEach(patient => {
              if (!patient.count || !patient.rate) return;

              const count = parseInt(patient.count || 0);
              const rate = parseFloat(patient.rate || 0);
              const subtotal = count * rate;
              const percentageRate = parseFloat(rateValue || 0);
              const patientAmount = subtotal * (percentageRate / 100);

              allPatients.push({
                count,
                rate,
                description: patient.description || '',
                amount: patientAmount
              });

              totalAmount += patientAmount;
            });
          }
        });

        // Create description combining all patient entries
        const description = allPatients.map(p => 
          `${p.count} × £${p.rate.toFixed(2)} @ ${rateValue}%${p.description ? ` (${p.description})` : ''}`
        ).join(' | ');

        return {
          date: formattedDate,
          formattedDate,
          description,
          amount: totalAmount,
          patients: allPatients,
          rateType,
          rateValue: parseFloat(rateValue || 0)
        };
      }
    });
  };

  // Modify the handleGeneratePDF function to update email workflow state
  const handleGeneratePDF = async () => {
    if (!previewHtml || !profileData) {
      toast({
        title: "Error",
        description: "Please generate a preview first and ensure your profile is complete",
        variant: "destructive",
      });
      return;
    }
    
    // Clear any existing progress timers to prevent infinite loops
    if (pdfProgressTimer) {
      clearTimeout(pdfProgressTimer);
      setPdfProgressTimer(null);
    }
    
    setPdfLoading(true);
    try {
      // Get practice details
      const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
      if (!practice) throw new Error('Practice not found');
      
      // If not saved yet or we have a preview number, save first
      let invoiceNum = currentInvoiceNumber;
      if (!invoiceSaved || previewInvoiceNumber) {
        const savedData = await saveInvoiceData();
        if (!savedData) {
          throw new Error('Failed to save invoice data');
        }
        invoiceNum = savedData.invoiceNumber;
      }

      // Create invoice data for PDF
      const invoiceData = {
        practice,
        profile: profileData,
        entries: formatEntriesForPDF(entries),
        total: total?.amount || 0,
        invoiceNumber: invoiceNum,
        rateType,
        rateValue: parseFloat(rateValue || 0)
      };

      // Update the filename for the email workflow BEFORE generating the PDF
      const practiceName = practice.prac_name.replace(/\s+/g, '_');
      const newPdfFilename = `${practiceName}_invoice_${invoiceNum}.pdf`;
      setEmailPdfFilename(newPdfFilename);
      
      // Generate PDF with the existing invoice number
      await generateInvoicePDF(invoiceData);
      
      // Now update the email workflow state to be ready
      setInvoiceSaved(true);
      setPdfGenerationStage('completed');
      setPdfGenerationProgress(100);
      
      toast({
        title: "Success",
        description: "PDF generated successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      setPdfGenerationStage('failed');
      setPdfGenerationProgress(100);
      
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Add the saveInvoiceData function that will be called by the Save button
  const saveInvoiceData = async () => {
    if (!profileData) {
      toast({
        title: "Error",
        description: "Please complete your profile before saving an invoice",
        variant: "destructive",
      });
      return;
    }

    if (!total) {
      toast({
        title: "Error",
        description: "Please calculate the total first",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Check if we've already saved this invoice and there are no changes
      const currentData = JSON.stringify({entries, rateType, rateValue, total});
      
      if (invoiceSaved && lastSavedData === currentData && currentInvoiceNumber) {
        console.log('No changes detected, reusing invoice number:', currentInvoiceNumber);
        toast({
          title: "No changes detected",
          description: "The invoice was already saved with no changes",
        });
        return { invoiceNumber: currentInvoiceNumber };
      }
      
      // Use cached preview number if available, otherwise generate new one
      let invoiceNum;
      if (previewInvoiceNumber) {
        console.log('Using cached preview number:', previewInvoiceNumber);
        invoiceNum = previewInvoiceNumber;
        setPreviewInvoiceNumber(null); // Clear the cache after using it
      } else {
        invoiceNum = await invoiceNumberService.generateInvoiceNumber(
          user.uid, 
          profileData,
          { forSaving: true, persistToDatabase: true }
        );
        console.log('Generated new invoice number:', invoiceNum);
      }

      // Call the existing handleSaveInvoice function with new number
      const savedData = await handleSaveInvoice();
      
      // Store the current data state and invoice number
      setInvoiceSaved(true);
      setLastSavedData(currentData);
      setCurrentInvoiceNumber(invoiceNum);
      
      return { ...savedData, invoiceNumber: invoiceNum };
    } catch (error) {
      console.error("Error in saveInvoiceData:", error);
      throw error;
    }
  };

  // Add effect to reset saved state when key invoice data changes
  useEffect(() => {
    // Reset saved state when entries, rate type, or rate value changes
    setInvoiceSaved(false);
    setLastSavedData(null);
  }, [entries, rateType, rateValue]);

  // Add debug logging to track what's happening
  useEffect(() => {
    if (showPracticeSettings) {
      console.log('MonthlyInvoice opening PracticeSettings with:', {
        selectedPracticesCount: selectedPractices?.length || 0,
        selectedPracticesData: selectedPractices
      });
    }
  }, [showPracticeSettings, selectedPractices]);

  // Function to show progress during PDF generation with proper delays
  const startProgressTracking = (initialStage = 'preparing') => {
    // First, clean up any existing timer to prevent infinite loops
    if (pdfProgressTimer) {
      clearTimeout(pdfProgressTimer);
      setPdfProgressTimer(null);
    }
    
    setPdfGenerationStage(initialStage);
    setPdfGenerationProgress(0);
    
    // Set up a timer that will update the progress
    const timer = setTimeout(() => {
      // Safety check - only proceed if we're still in a valid state
      if (initialStage !== 'completed' && initialStage !== 'failed') {
        updateProgressStep(initialStage, 0);
      }
    }, 200);
    
    setPdfProgressTimer(timer);
  };

  // Modify updateProgressStep to be more resilient against infinite loops
  const updateProgressStep = (stage, currentProgress) => {
    // Early exit conditions to prevent infinite loops
    if (stage === 'completed' || stage === 'failed' || currentProgress >= 95) {
      setPdfGenerationStage(stage === 'failed' ? 'failed' : 'completed');
      setPdfGenerationProgress(100);
      return;
    }

    // Update the progress based on current stage
    let nextProgress;
    let nextStage = stage;
    let stageDelay = 500;
    
    // Calculate next progress with hard limits to ensure we always advance
    switch (stage) {
      case 'preparing':
        nextProgress = Math.min(currentProgress + 3, 30);
        if (nextProgress >= 30) {
          nextStage = 'generating';
          stageDelay = 800;
        }
        break;
      case 'generating':
        nextProgress = Math.min(currentProgress + 2, 70);
        if (nextProgress >= 70) {
          nextStage = 'downloading';
          stageDelay = 800;
        }
        break;
      case 'downloading':
        nextProgress = Math.min(currentProgress + 3, 95);
        stageDelay = 600;
        break;
      default:
        // Fallback to ensure we make progress even in unknown states
        nextProgress = Math.min(currentProgress + 2, 95);
    }
    
    // Update state
    setPdfGenerationStage(nextStage);
    setPdfGenerationProgress(nextProgress);
    
    // Safety check - ensure we're not setting an infinite loop
    // Only continue if we've made progress
    if (nextProgress > currentProgress && nextProgress < 95) {
      const timer = setTimeout(() => {
        // Additional safety check before recursion
        if (nextStage !== 'completed' && nextStage !== 'failed') {
          updateProgressStep(nextStage, nextProgress);
        }
      }, nextStage === stage ? 150 : stageDelay);
      
      setPdfProgressTimer(timer);
    } else {
      // Force completion if we didn't make progress
      completeProgressTracking(true);
    }
  };

  // Complete the progress tracking with success or failure and enforced delay
  const completeProgressTracking = (success = true) => {
    // Clear any existing timer
    if (pdfProgressTimer) {
      clearTimeout(pdfProgressTimer);
      setPdfProgressTimer(null);
    }
    
    // First bring the progress to 95% if it's not already there
    setPdfGenerationProgress(95);
    
    // Then wait a moment before finalizing (enforced delay)
    setTimeout(() => {
      // Set final state
      setPdfGenerationStage(success ? 'completed' : 'failed');
      setPdfGenerationProgress(100);
      
      // If success, verify filename is properly set
      if (success && currentInvoiceNumber) {
        const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
        if (practice) {
          const practiceName = practice.prac_name.replace(/\s+/g, '_');
          const updatedFilename = `${practiceName}_invoice_${currentInvoiceNumber}.pdf`;
          
          // Make sure filename is set
          setEmailPdfFilename(updatedFilename);
        }
      }
    }, 1000); // Enforced 1-second delay before completing
  };

  // Progress bar component
  const PdfGenerationProgress = () => {
    // Get appropriate text for current stage
    const stageText = {
      'not_started': 'Ready to generate PDF',
      'preparing': 'Preparing data...',
      'generating': 'Creating PDF document...',
      'downloading': 'Finalizing download...',
      'completed': 'PDF Downloaded Successfully',
      'failed': 'PDF Generation Failed'
    }[pdfGenerationStage];

    // Get appropriate color for current stage
    const stageColor = pdfGenerationStage === 'failed' ? 
      'bg-red-600' : 
      (pdfGenerationStage === 'completed' ? 'bg-green-600' : 'bg-purple-600');

    return (
      <div className="w-full space-y-2">
        <div className="flex justify-between text-xs text-gray-400">
          <span>{stageText}</span>
          <span>{pdfGenerationProgress}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2.5 overflow-hidden">
          <div 
            className={`h-2.5 rounded-full transition-all duration-300 ${stageColor}`} 
            style={{width: `${pdfGenerationProgress}%`}}
          ></div>
        </div>
        <div className="flex justify-between mt-3">
          <div className={`text-xs ${pdfGenerationStage === 'preparing' || pdfGenerationStage === 'completed' ? 'text-purple-300' : 'text-gray-500'}`}>
            Preparing
          </div>
          <div className={`text-xs ${pdfGenerationStage === 'generating' ? 'text-purple-300' : 'text-gray-500'}`}>
            Generating
          </div>
          <div className={`text-xs ${pdfGenerationStage === 'downloading' || pdfGenerationStage === 'completed' ? 'text-purple-300' : 'text-gray-500'}`}>
            Downloading
          </div>
        </div>
      </div>
    );
  };

  // Add a cleanup effect for the progress timer
  useEffect(() => {
    return () => {
      // Clean up timer when component unmounts
      if (pdfProgressTimer) {
        clearTimeout(pdfProgressTimer);
      }
    };
  }, [pdfProgressTimer]);

  // Add this new function that's referenced in your component
  const handleSaveAndGeneratePDF = async () => {
    setPdfLoading(true);
    try {
      // Generate preview first if needed
      if (!previewGenerated) {
        await handleGeneratePreview();
      }
      
      // First save the invoice data
      const savedData = await saveInvoiceData();
      if (!savedData) {
        throw new Error('Failed to save invoice data');
      }
      
      // Get practice details
      const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
      if (!practice) throw new Error('Practice not found');
      
      // Create invoice data for PDF
      const invoiceData = {
        practice,
        profile: profileData,
        entries: formatEntriesForPDF(entries),
        total: total?.amount || 0,
        invoiceNumber: savedData.invoiceNumber || currentInvoiceNumber,
        rateType,
        rateValue: parseFloat(rateValue || 0)
      };
      
      // Update the filename state variable before generating PDF
      const practiceName = practice.prac_name.replace(/\s+/g, '_');
      const newPdfFilename = `${practiceName}_invoice_${savedData.invoiceNumber || currentInvoiceNumber}.pdf`;
      setEmailPdfFilename(newPdfFilename);
      
      // Generate PDF
      await generateInvoicePDF(invoiceData);
      
      toast({
        title: "Success",
        description: "PDF generated successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Add this state variable with the other state declarations at the top of the component
  const [saveConfirmation, setSaveConfirmation] = useState(null);

  // Add this effect to ensure dropdown re-renders when enhancedPractices updates
  const [dropdownKey, setDropdownKey] = useState(0);

  // Remove showPracticeSettings from dependencies of any useEffect hooks

  // Remove the memoized version and keep it as a regular function
  const renderPracticeSettings = () => {
    if (!showPracticeSettings) return null;
    
    return (
      <Dialog open={showPracticeSettings} onOpenChange={setShowPracticeSettings}>
        <DialogContent className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium text-white">Manage Practices</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <PracticeSettings 
              initialSelectedPractices={selectedPractices}
              onSave={handleSavePractices}
              onClose={() => setShowPracticeSettings(false)}
              user={user}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Modify this effect to only handle basic UI state for practice details
  useEffect(() => {
    // Only handle local UI state based on selection
    if (!selectedPractice) {
      setPracticeDetails(null);
      return;
    }

    // Get practice details from props
    const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
    setPracticeDetails(practice);
  }, [selectedPractice, selectedPractices]);

  // PREVENT INFINITE RENDER LOOPS - with modifications
  const previousProps = useRef({
    selectedPractices: null,
    selectedPractice: null
  });
  
  // More robust render tracking that checks if props are the cause
  useEffect(() => {
    renderCount.current += 1;
    
    // Log only every 5th render to reduce noise
    if (renderCount.current % 5 === 0) {
      console.log('MonthlyInvoice render count:', renderCount.current);
      
      // Compare with previous props to find what's changing
      if (previousProps.current.selectedPractice !== selectedPractice) {
        console.log('selectedPractice changed:', previousProps.current.selectedPractice, '->', selectedPractice);
      }
      
      if (previousProps.current.selectedPractices !== selectedPractices) {
        console.log('selectedPractices array reference changed');
      }
    }
    
    // Store current props for next comparison
    previousProps.current = {
      selectedPractices,
      selectedPractice
    };
    
    // Break infinite loops by throwing if we render too many times
    if (renderCount.current > 50) {  // Increased threshold to be more forgiving
      console.error('Too many re-renders in MonthlyInvoice - breaking the loop');
      throw new Error('Breaking render loop in MonthlyInvoice');
    }
  });

  // Fix getPractice wrapper to properly memoize and only call when needed
  const getAndCachePractice = useMemo(() => {
    return async (code) => {
      if (!code) return null;
      try {
        return await getPractice(code);
      } catch (error) {
        console.error('Error in getAndCachePractice:', error);
        return null;
      }
    };
  }, [getPractice]);

  // IMPORTANT: Remove any calls to async functions or state updates from render
  // Move them to useEffect hooks with proper dependencies

  // IMPORTANT: Remove console.log statement from the JSX/render method
  return (
    <div className="space-y-4">
          {showPracticeSettings && (
            <Dialog open={showPracticeSettings} onOpenChange={setShowPracticeSettings}>
              <DialogContent className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
                <DialogHeader>
                  <DialogTitle className="text-lg font-medium text-white">Manage Practices</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <PracticeSettings 
                    initialSelectedPractices={selectedPractices}
                    onSave={handleSavePractices}
                    onClose={() => setShowPracticeSettings(false)}
                    user={user}
                  />
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Practice Selection Card */}
          <Card className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
            <CardContent className="p-3 sm:p-4">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-medium text-white">Select Practice</h2>
              </div>
              
              <div className="grid grid-cols-1 gap-2 sm:gap-4">
                <div className="flex items-end gap-2">
                  <div className="flex-grow">
                    <Select 
                      value={selectedPractice} 
                      onValueChange={handlePracticeChange}
                      key={`practice-select-${dropdownKey}`}
                    >
                      <SelectTrigger id="practice" className="bg-[#1F1F2E] border-purple-500/30 text-white text-xs sm:text-sm">
                        <SelectValue placeholder={selectedPractices.length > 0 ? "Select practice" : "No practices available"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#2A2A3E] border-purple-500/30">
                        {selectedPractices.length > 0 ? (
                          selectedPractices.map(practice => (
                            <SelectItem 
                              key={practice.prac_code} 
                              value={practice.prac_code}
                              className="text-white hover:bg-purple-500/20 text-xs sm:text-sm"
                            >
                              {practice.prac_name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="none" disabled className="text-gray-500 hover:bg-transparent">
                            Please add practices first
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={() => setShowPracticeSettings(true)}
                    size="sm" 
                    variant="outline"
                    className="border-purple-500/30 bg-purple-900/20 text-purple-300 h-8 sm:h-9"
                  >
                    <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="ml-1 text-xs">Manage</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Details Card */}
          <Card className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
            <CardContent className="p-3">
              <h2 className="text-base font-medium text-white mb-2">Invoice Details</h2>
              <div className="flex items-center gap-2">
                <div className="flex-grow">
                  <Label htmlFor="invoiceType" className="text-xs text-gray-300 mb-1 block">Invoice Type</Label>
                  <Select value={rateType} onValueChange={setRateType}>
                    <SelectTrigger id="invoiceType" className="bg-[#1F1F2E] border-purple-500/30 text-white text-xs h-9">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2A2A3E] border-purple-500/30">
                      <SelectItem value="percentage" className="text-white hover:bg-purple-500/20">Percentage</SelectItem>
                      <SelectItem value="hourly" className="text-white hover:bg-purple-500/20">Hourly</SelectItem>
                      <SelectItem value="fixed" className="text-white hover:bg-purple-500/20">Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {rateType && (
                  <div className="w-24">
                    <Label htmlFor="rateValue" className="text-xs text-gray-300 mb-1 block">
                      {rateType === 'percentage' ? '%' : '£'}
                    </Label>
                    <Input
                      id="rateValue"
                      type="number"
                      value={rateValue}
                      onChange={(e) => setRateValue(e.target.value)}
                      className="bg-[#1F1F2E] border-purple-500/30 text-white text-xs h-9"
                      placeholder="Value"
                      step={rateType === 'percentage' ? '1' : '0.01'}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Validation Error */}
          {validationError && (
            <Alert variant="destructive" className="bg-red-900/20 border-red-500/50 text-red-200">
              <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription className="text-xs sm:text-sm">{validationError}</AlertDescription>
            </Alert>
          )}
          
          {/* Entry Card - REVISED for clarity and mobile optimization */}
          <Card className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
            <CardContent className="p-3 sm:p-4">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-medium text-white">Entry Details</h2>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleAddEntry}
                  className="text-2xs sm:text-xs border-purple-500/30 bg-purple-900/20 text-purple-300"
                >
                  <PlusIcon className="h-3 w-3 mr-1" /> Add Entry
                </Button>
              </div>
              
              {/* Direct rendering of entries instead of memoized entriesDisplay */}
              {(entries && entries.length > 0 ? entries : [{
                date: null,
                isExpanded: true,
                patients: rateType === 'percentage' ? [{ count: '', rate: '', description: '' }] : [],
                hours: '',
                description: ''
              }]).map((entry, entryIndex) => (
                <div 
                  key={`entry-${entryIndex}`} 
                  className="bg-[#1F1F2E] p-2 sm:p-3 rounded-lg border border-purple-500/10 mb-3"
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleEntry(entryIndex)}
                        className="p-1 h-6 w-6 sm:h-7 sm:w-7 mr-1"
                      >
                        {entry.isExpanded ? (
                          <ChevronUpIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                        )}
                      </Button>
                      <h3 className="text-xs sm:text-sm font-medium text-white">
                        {entry.date ? format(entry.date, 'dd MMM yyyy') : 'New Entry'}
                      </h3>
                    </div>
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveEntry(entryIndex)}
                        disabled={entries.length <= 1}
                        className="h-8 w-8 bg-red-900/10 text-red-400 hover:text-red-300 hover:bg-red-900/20 border border-red-500/20 p-0 flex items-center justify-center"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {entry.isExpanded && (
                    <div className="pl-5 sm:pl-7 mt-2 sm:mt-3 space-y-2 sm:space-y-4">
                      {/* First row - always show date with improved date picker */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-gray-300 mb-1 block">Date</Label>
                          <Popover open={calendarOpen === entryIndex} onOpenChange={(isOpen) => setCalendarOpen(isOpen ? entryIndex : null)}>
                            <PopoverTrigger asChild>
                              <div
                                className={cn(
                                  "flex h-9 w-full rounded-md border border-gray-700 bg-[#2A2A3E] px-3 py-2 text-sm text-white shadow-sm hover:bg-[#1F1F2E] cursor-pointer",
                                  !entry.date && "text-gray-400"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {entry.date ? format(entry.date, "dd/MM/yyyy") : "Select date"}
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[#2A2A3E] border-purple-500/30">
                              <EnhancedDatePicker 
                                selected={entry.date}
                                onSelect={(date) => handleDateChange(entryIndex, date)}
                                entryIndex={entryIndex}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        {/* Hours field - only show for hourly, fixed, or when hours already exist */}
                        {(rateType === 'hourly' || rateType === 'fixed' || entry.hours) && (
                          <div>
                            <Label htmlFor={`hours-${entryIndex}`} className="text-xs text-gray-300 mb-1 block">Hours</Label>
                            <Input
                              id={`hours-${entryIndex}`}
                              type="number"
                              value={entry.hours || ''}
                              onChange={(e) => handleEntryChange(entryIndex, 'hours', e.target.value)}
                              className="bg-[#2A2A3E] border-gray-700 text-white text-sm"
                              step="0.5"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Patient fields section */}
                      {rateType === 'percentage' && (
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <Label className="text-xs text-gray-300">Multiple Patient/Rate Types</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddPatient(entryIndex)}
                              className="h-7 border border-purple-500/30 bg-purple-900/20 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200 active:bg-purple-900/20"
                            >
                              <PlusIcon className="h-4 w-4" />
                              <span className="text-xs ml-1">Add Rate Type</span>
                            </Button>
                          </div>
                          
                          <p className="text-xs text-gray-400 mb-3">Add different patient counts with their respective rates for this date</p>
                          
                          {entry.patients && entry.patients.map((patient, patientIndex) => (
                            <div 
                              key={`patient-${entryIndex}-${patientIndex}`}
                              className="grid grid-cols-12 gap-2 mb-2 items-end"
                            >
                              <div className="col-span-2">
                                <Label htmlFor={`count-${entryIndex}-${patientIndex}`} className="text-xs text-gray-400 mb-1 block">Count</Label>
                                <Input
                                  id={`count-${entryIndex}-${patientIndex}`}
                                  type="number"
                                  value={patient.count || ''}
                                  onChange={(e) => handlePatientChange(entryIndex, patientIndex, 'count', e.target.value)}
                                  className="bg-[#2A2A3E] border-gray-700 text-white text-sm"
                                />
                              </div>
                              
                              <div className="col-span-2">
                                <Label htmlFor={`rate-${entryIndex}-${patientIndex}`} className="text-xs text-gray-400 mb-1 block">Rate</Label>
                                <Input
                                  id={`rate-${entryIndex}-${patientIndex}`}
                                  type="number"
                                  value={patient.rate || ''}
                                  onChange={(e) => handlePatientChange(entryIndex, patientIndex, 'rate', e.target.value)}
                                  className="bg-[#2A2A3E] border-gray-700 text-white text-sm"
                                />
                              </div>
                              
                              <div className="col-span-7">
                                <Label htmlFor={`description-${entryIndex}-${patientIndex}`} className="text-xs text-gray-400 mb-1 block">Description</Label>
                                <Input
                                  id={`description-${entryIndex}-${patientIndex}`}
                                  value={patient.description || ''}
                                  onChange={(e) => handlePatientChange(entryIndex, patientIndex, 'description', e.target.value)}
                                  className="bg-[#2A2A3E] border-gray-700 text-white text-sm"
                                />
                              </div>
                              
                              <div className="col-span-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemovePatient(entryIndex, patientIndex)}
                                  disabled={!entry.patients || entry.patients.length <= 1}
                                  className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-900/20 p-0 flex items-center justify-center"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Show description field for hourly and fixed rates */}
                      {(rateType === 'hourly' || rateType === 'fixed') && (
                        <div>
                          <Label htmlFor={`description-${entryIndex}`} className="text-xs text-gray-300 mb-1 block">Description</Label>
                          <Input
                            id={`description-${entryIndex}`}
                            value={entry.description || ''}
                            onChange={(e) => handleEntryChange(entryIndex, 'description', e.target.value)}
                            className="bg-[#2A2A3E] border-gray-700 text-white text-sm"
                            placeholder="Description of work"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              <div className="mt-3 sm:mt-4 flex justify-end">
                <Button 
                  onClick={validateAndCalculateTotal}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 sm:px-6 text-xs sm:text-sm font-medium"
                >
                  Calculate Total
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Invoice Summary Card */}
          <Card className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
            <CardContent className="p-4">
              <h2 className="text-lg font-medium text-white mb-4">Invoice Summary</h2>
              
              <div className="bg-[#1F1F2E] p-4 rounded-lg mb-4">
                <div className="flex items-center justify-between">
                  {/* Show patients count for percentage rate */}
                  {rateType === 'percentage' && (
                    <div className="flex-1">
                      <p className="text-sm text-gray-400">Total Patients</p>
                      <p className="text-lg font-medium text-white">{total?.patients || 0}</p>
                    </div>
                  )}
                  
                  {/* Show hours for hourly or fixed rate types */}
                  {rateType !== 'percentage' && (
                    <div className="flex-1">
                      <p className="text-sm text-gray-400">Total Hours</p>
                      <p className="text-lg font-medium text-white">{total?.hours || 0}</p>
                    </div>
                  )}
                  
                  {/* Always show amount - larger on mobile */}
                  <div className="flex-1 text-right">
                    <p className="text-sm text-gray-400">Total Amount</p>
                    <p className="text-lg font-medium text-white">{formatCurrency(total?.amount || 0)}</p>
                  </div>
                </div>
              </div>
              
              {summary.length > 0 && (
                <div className="bg-[#1F1F2E] p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-purple-300 mb-2">Detailed Summary</h3>
                  <ul className="space-y-1">
                    {summary.map((item, index) => (
                      <li 
                        key={index} 
                        className={cn(
                          "text-sm",
                          item.isSubtotal || item.isPercentage || item.isTotal 
                            ? "text-white font-semibold border-t border-purple-500/30 pt-2 mt-2" 
                            : "text-gray-300"
                        )}
                      >
                        <span className={cn(
                          item.isSubtotal || item.isPercentage || item.isTotal 
                            ? "text-purple-300" 
                            : "text-gray-400"
                        )}>
                          {item.label}:
                        </span> {item.value}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="mt-4 flex justify-end">
                <Button 
                  onClick={handleGeneratePreview} 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                  disabled={!total || previewLoading}
                >
                  {previewLoading ? <LoadingSpinner size="small" /> : "Generate Invoice Preview"}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Invoice Preview */}
          {previewHtml && (
            <Card className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
              <CardContent className="p-4">
                <h2 className="text-lg font-medium text-white mb-4">Invoice Preview</h2>
                
                <div className="bg-white rounded-lg p-4 overflow-auto max-h-[600px]">
                  <div 
                    className="invoice-preview" 
                    dangerouslySetInnerHTML={{ __html: previewHtml }} 
                  />
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Invoice Actions - NEW TWO-STEP EMAIL WORKFLOW */}
          {entries.filter(entry => entry.date).length > 0 && (
            <Card className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
              <CardContent className="p-4">
                <h2 className="text-lg font-medium text-white mb-4">Invoice Actions</h2>
                
                <div className="flex flex-col space-y-3">
                  <Button
                    variant="default"
                    onClick={handleGeneratePDF}
                    disabled={pdfLoading || !previewGenerated}
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white hover:text-white flex items-center justify-center"
                  >
                    {pdfLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        <FileDown className="mr-2 h-4 w-4" />
                        Save and Download PDF
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSaving(true);
                      setSaveConfirmation(null); // Clear any existing message
                      saveInvoiceData()
                        .then(() => {
                          const message = invoiceSaved && lastSavedData === JSON.stringify({entries, rateType, rateValue, total}) 
                            ? "No changes detected" 
                            : "Invoice saved successfully";
                          
                          setSaveConfirmation({
                            text: message,
                            type: 'success'
                          });
                          
                          // Clear the message after 3 seconds
                          setTimeout(() => {
                            setSaveConfirmation(null);
                          }, 3000);
                        })
                        .catch((error) => {
                          console.error('Error saving invoice:', error);
                          setSaveConfirmation({
                            text: "Failed to save invoice",
                            type: 'error'
                          });
                          
                          // Clear error message after 5 seconds
                          setTimeout(() => {
                            setSaveConfirmation(null);
                          }, 5000);
                        })
                        .finally(() => {
                          setSaving(false);
                        });
                    }}
                    disabled={saving || pdfLoading || !previewGenerated}
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white hover:text-white flex items-center justify-center"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {invoiceSaved ? "Save" : "Save"}
                      </>
                    )}
                  </Button>
                  
                  {/* Add save confirmation message */}
                  {saveConfirmation && (
                    <div className={cn(
                      "mt-2 px-3 py-1 text-sm flex items-center rounded-md animate-fade-in",
                      saveConfirmation.type === 'success' ? "bg-green-500/20 text-green-300 border border-green-500/30" : 
                      "bg-red-500/20 text-red-300 border border-red-500/30"
                    )}>
                      <div className={cn(
                        "w-4 h-4 mr-2 rounded-full flex items-center justify-center",
                        saveConfirmation.type === 'success' ? "bg-green-500/30" : "bg-red-500/30"
                      )}>
                        {saveConfirmation.type === 'success' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      {saveConfirmation.text}
                    </div>
                  )}
                  
                  {/* Email Workflow Section - New Two-Step Approach */}
                  <div className="mt-2 bg-[#1F1F2E] p-3 rounded-lg border border-purple-500/20">
                    <h3 className="text-sm font-medium text-purple-300 mb-3">Email Invoice</h3>
                    
                    <div className="text-xs text-gray-300 mb-3">
                      To email this invoice, first download the PDF and then open the email template:
                    </div>
                    
                    <div className="space-y-3">
                      {/* Step 1: Generate PDF Button */}
                      <div className="flex flex-row items-center">
                        <div className="bg-purple-500 text-white rounded-full w-5 h-5 inline-flex items-center justify-center mr-2 text-xs">1</div>
                        <div className="flex-grow">
                          {invoiceSaved && currentInvoiceNumber && emailPdfFilename ? (
                            <div className="flex items-center text-green-300 text-xs">
                              <div className="bg-green-500/20 p-1 rounded-full mr-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              PDF: <span className="ml-1 font-medium">{emailPdfFilename}</span>
                            </div>
                          ) : (
                            <Button 
                              onClick={handleSaveAndGeneratePDF}
                              disabled={pdfLoading || !previewGenerated}
                              className="bg-purple-600 hover:bg-purple-700 text-white text-xs w-full sm:w-auto"
                            >
                              {pdfLoading ? (
                                <span className="flex items-center">
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Generating PDF...
                                </span>
                              ) : (
                                <span className="flex items-center">
                                  <FileDown className="mr-2 h-4 w-4" />
                                  Generate PDF
                                </span>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Step 2: Open Email Template Button */}
                      <div className="flex flex-row items-center">
                        <div className="bg-purple-500 text-white rounded-full w-5 h-5 inline-flex items-center justify-center mr-2 text-xs">2</div>
                        <div className="flex-grow">
                          <Button
                            onClick={openEmailTemplate}
                            disabled={!invoiceSaved || !currentInvoiceNumber || !emailPdfFilename}
                            className={cn(
                              "text-xs w-full sm:w-auto",
                              invoiceSaved && currentInvoiceNumber && emailPdfFilename
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "bg-gray-700 text-gray-400 cursor-not-allowed"
                            )}
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            Open Email Template
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Warning about attachment */}
                    <div className="mt-3 bg-amber-500/10 p-2 rounded-md border border-amber-500/30 text-xs text-amber-300">
                      <p>
                        <span className="font-bold">Note:</span> Due to browser security restrictions, you'll need to manually attach the PDF file to your email before sending.
                        {emailPdfFilename && (
                          <> The filename is: <span className="font-medium">{emailPdfFilename}</span></>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
    </div>
  );
};

// ANTI-LOOP: Create a more robust comparison function
function areEqual(prevProps, nextProps) {
  // Compare props that would cause visual changes
  return (
    prevProps.selectedPractice === nextProps.selectedPractice &&
    prevProps.selectedPractices === nextProps.selectedPractices &&
    prevProps.previewHtml === nextProps.previewHtml &&
    prevProps.rateType === nextProps.rateType &&
    prevProps.rateValue === nextProps.rateValue &&
    // Add any other props that would cause a meaningful UI change
    prevProps.previewLoading === nextProps.previewLoading &&
    prevProps.pdfLoading === nextProps.pdfLoading
  );
}

// ANTI-LOOP: Export a memoized version with our custom comparison
export default React.memo(MonthlyInvoice, areEqual);
